import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSetSelectedAudioNoteId, useSetSidePanelOpen } from '@renderer/stores/side-panel.ts'
import { useInsightActions } from '@renderer/hooks/useInsightActions.ts'
import {
  AnimatedLabel,
  ContextMenu,
  FadingScrollable,
  FinalizedAttachment,
  MeetingIcon,
  MenuItemType,
  useActiveChatId,
  useAttachmentStore,
  useExternalFocusChatInput,
} from '@renderer/chat'
import { removeFromInsightsWhitelist } from '@shared/store/impl/insightsVisibilityPreferences.ts'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer.ts'
import {
  ArrowLeft,
  ArrowUpRight,
  Ban,
  Check,
  Copy,
  EllipsisVertical,
  Link2Off,
  MailCheck,
  MailMinus,
  Trash2,
  Mic,
} from 'lucide-react'
import { motion } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { useTodaysInsightsBySource } from '@renderer/windows/features/home/hooks/use-insights'
import { InsightWithSource } from '@shared/utils/insights/types.ts'
import { useCopyInsightsToClipboard } from '@renderer/hooks/insights.ts'
import { Modal, useModalContext } from '../index'
import { Button, Dialog, Tooltip } from '@highlight/ui'
import { InsightListItem } from '@renderer/windows/features/home/components/insights-section/insight-list-item'
import { appIconUrl } from '@renderer/utils/app-icon'

export const HighlightsStackModal = () => {
  const { context, close } = useModalContext('highlights-stack')
  const { group } = context
  const { data: grouped } = useTodaysInsightsBySource()

  const setSidePanelOpen = useSetSidePanelOpen()
  const setSelectedAudioNoteId = useSetSelectedAudioNoteId()
  const copyToClipboard = useCopyInsightsToClipboard()
  const { bulkDeleteInsights, bulkPatchInsights } = useInsightActions()
  const { addInsightAttachments, removeAttachments } = useAttachmentStore()
  const externalFocusInput = useExternalFocusChatInput()
  const [copied, setCopied] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const activeChatId = useActiveChatId()

  const insights = (grouped as Record<string, Array<InsightWithSource>>)[group] || []
  const hasInsights = insights.length > 0
  const insight = hasInsights ? insights[0] : null
  const meetingNote = insight?.meetingNote

  const icon = useMemo(() => {
    if (!insight) return undefined
    if (insight.source === 'audio-note') {
      if (insight.meetingNote?.meeting) {
        return <MeetingIcon appName={insight.meetingNote.meeting.app.name} size={24} />
      } else {
        return <Mic size={24} className="text-green" />
      }
    } else if (insight.screenSource?.appName) {
      return (
        <img
          src={appIconUrl(insight.screenSource.appName)}
          alt={insight.screenSource.appName}
          className="size-6 object-contain"
        />
      )
    }
    return undefined
  }, [insight])

  const isFromArtifact = useMemo(() => {
    return !!meetingNote
  }, [meetingNote])

  const ignoreFromApp = useCallback(() => {
    const appName = insight?.screenSource?.appName
    if (!appName) {
      return
    }
    removeFromInsightsWhitelist(appName, 'app')
    AmplitudeRenderer.trackEvent('Insights Action', {
      action: 'Ignore from app',
      appName: appName,
      type: insight?.meetingNote ? 'Meeting note' : 'Screen',
      count: insights.length,
      source: 'Stack modal',
    })
  }, [insight, insights.length])

  const ignoreFromDomain = useCallback(() => {
    const url = insight?.screenSource?.url
    if (!url) {
      return
    }
    removeFromInsightsWhitelist(new URL(url).hostname, 'domain')
    AmplitudeRenderer.trackEvent('Insights Action', {
      action: 'Ignore from domain',
      type: insight?.meetingNote ? 'Meeting note' : 'Screen',
      count: insights.length,
      source: 'Stack modal',
    })
  }, [insight, insights.length])

  const menuItems = useMemo(() => {
    if (!insight) return []
    return [
      {
        label: (
          <>
            {insight.status === 'completed' ? <MailMinus size={18} /> : <MailCheck size={18} />}
            {insight.status === 'completed' ? 'Mark all unresolved' : 'Mark all resolved'}
          </>
        ),
        onClick: () => {
          bulkPatchInsights(
            insights.map(({ id }) => id),
            { status: insight.status === 'completed' ? 'active' : 'completed' },
          )
          AmplitudeRenderer.trackEvent('Insights Action', {
            action: insight.status === 'completed' ? 'Unresolve' : 'Resolve',
            type: insight.meetingNote ? 'Meeting note' : 'Screen',
            count: insights.length,
            source: 'Stack modal',
          })
        },
      },
      ...(insight.screenSource?.url
        ? [
            {
              label: (
                <>
                  <Link2Off size={18} /> Ignore from {new URL(insight.screenSource.url).hostname}
                </>
              ),
              onClick: ignoreFromDomain,
            },
          ]
        : []),
      ...(insight.screenSource?.appName
        ? [
            {
              label: (
                <>
                  <Ban size={18} /> Ignore from {insight.screenSource?.appName}
                </>
              ),
              onClick: ignoreFromApp,
            },
          ]
        : []),
      {
        label: (
          <div className={'text-red flex items-center gap-2'}>
            <Trash2 size={18} /> Delete all
          </div>
        ),
        onClick: () => {
          setDeleteModalOpen(true)
        },
      },
    ] as Array<MenuItemType>
  }, [insight, insights, bulkPatchInsights, ignoreFromDomain, ignoreFromApp])

  const onClickArtifact = () => {
    if (!isFromArtifact) {
      return
    }
    if (meetingNote) {
      setSelectedAudioNoteId(meetingNote.id)
    }

    setSidePanelOpen(true)

    AmplitudeRenderer.trackEvent('Insights Action', {
      action: 'Open artifact',
      type: meetingNote ? 'Meeting note' : 'Screen',
      count: insights.length,
      source: 'Stack modal',
    })
  }

  const onDeleteAll = () => {
    bulkDeleteInsights(insights.map(({ id }) => id))
    close()
    AmplitudeRenderer.trackEvent('Insights Action', {
      action: 'Delete',
      type: meetingNote ? 'Meeting note' : 'Screen',
      count: insights.length,
      source: 'Stack modal',
    })
  }

  const onCopyAll = useCallback(() => {
    copyToClipboard(insights, { source: 'Stack modal' })

    setCopied(true)
    setTimeout(() => {
      setCopied(false)
    }, 1500)
  }, [insights])

  // Automatically add insight attachments on mount, and remove
  // them when unmounting (keeping original intact).
  useEffect(() => {
    addInsightAttachments(...insights.map(({ id }) => id))
    externalFocusInput()
    return () => {
      removeAttachments((attachment) => {
        if (attachment.attachment?.type !== 'insight') {
          return true
        }
        const insight = attachment.attachment as Extract<FinalizedAttachment, { type: 'insight' }>
        return !insights.some(({ id }) => id === insight.insightId)
      })
    }
  }, [])

  useEffect(() => {
    if (activeChatId || !insights?.length) {
      close()
    }
  }, [activeChatId, insights, close])

  // Close modal if no insights remain
  useEffect(() => {
    if (!hasInsights) {
      close()
    }
  }, [hasInsights, close])

  // Return early if no insights
  if (!hasInsights) {
    return null
  }

  return (
    <>
      <Modal>
        <Modal.Content
          showClose={false}
          backdropClassName="fixed left-0 top-0 flex h-full w-full flex-shrink-0 justify-center bg-gradient-to-b from-[#1d1d1d]/80 to-[#1d1d1d] to-40% px-8 pb-0 pt-32 backdrop-blur-sm"
          className="flex w-full max-w-[800px] flex-col items-center gap-2 rounded-2xl border-none bg-transparent pb-[116px] pt-6 shadow-none"
        >
          <div className={'relative flex w-full items-center justify-between'} onClick={(e) => e.stopPropagation()}>
            <Modal.Close variant="ghost" shape="circle" size="sm">
              <ArrowLeft />
            </Modal.Close>
            <div className={'text-md flex w-full items-center gap-1 px-2.5'}>
              Highlights from
              <Tooltip disabled={!meetingNote}>
                <Tooltip.Trigger
                  render={
                    <div
                      id={'person-picker'}
                      className={twMerge(
                        'group/source flex items-center gap-1 rounded-lg p-1 pe-2',
                        isFromArtifact && 'hover:bg-yellow/10 hover:text-yellow-subtle cursor-pointer',
                      )}
                      onClick={onClickArtifact}
                    >
                      {icon && <div className={'inline-flex h-6 w-6 flex-shrink-0'}>{icon}</div>}
                      <span className={'text-md max-w-[400px] truncate'}>
                        {meetingNote?.title ?? insight?.assignedBy ?? insight?.screenSource?.appName}
                      </span>
                      {isFromArtifact && <ArrowUpRight size={16} />}
                    </div>
                  }
                />
                <Tooltip.Content side="bottom">View audio notes</Tooltip.Content>
              </Tooltip>
            </div>
            <div className={'flex items-center gap-1'}>
              <Tooltip>
                <Tooltip.Trigger
                  render={
                    <Button
                      aria-label="Copy as list"
                      shape="circle"
                      variant="ghost"
                      size="sm"
                      onClick={onCopyAll}
                      className={twMerge(copied && 'opacity-100')}
                    >
                      <AnimatedLabel
                        label={copied ? <Check key="copy-all-check" /> : <Copy key="copy-all" />}
                        transitionMs={100}
                        className={'flex'}
                      />
                    </Button>
                  }
                />
                <Tooltip.Content>{copied ? 'Copied!' : 'Copy as list'}</Tooltip.Content>
              </Tooltip>

              <ContextMenu items={menuItems} position={'bottom'} triggerId={'stack-more-options'} leftClick={true}>
                <Button variant="secondary" shape="circle" size="sm" id={'stack-more-options'}>
                  <EllipsisVertical size={18} />
                </Button>
              </ContextMenu>
            </div>
          </div>

          <FadingScrollable className="flex max-h-full min-h-0 w-full justify-center py-4 pb-12" fadeSize="2%">
            <div
              className={'flex h-max min-h-0 w-full max-w-[848px] flex-col gap-4 px-6'}
              onClick={(e) => e.stopPropagation()}
            >
              {insights?.map((insight, i) => (
                <motion.div
                  key={insight.id}
                  layout="position" // only animate position, not size/opacity
                  transition={{
                    layout: {
                      type: 'spring',
                      stiffness: 500,
                      damping: 40,
                      mass: 0.5,
                    },
                  }}
                >
                  <InsightListItem index={i} meetingNote={meetingNote} showPreview={true} {...insight} />
                </motion.div>
              ))}
            </div>
          </FadingScrollable>

          <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
            <Dialog.Content showClose={false}>
              <Dialog.Header>
                <Dialog.Title>Are you sure?</Dialog.Title>
                <Dialog.Description>
                  You are about to delete {insights.length} Highlights. This cannot be undone.
                </Dialog.Description>
              </Dialog.Header>
              <Dialog.Footer>
                <Dialog.Close variant="outline">Cancel</Dialog.Close>
                <Dialog.Close variant="primary" tone="danger" onClick={onDeleteAll}>
                  Delete all
                </Dialog.Close>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog>
        </Modal.Content>
      </Modal>
    </>
  )
}
